import { useQuery } from '@apollo/client';
import { t } from '@lingui/core/macro';
import { ButtonGroup, FilterButton, Skeleton } from '@pocketlaw/tetris';
import { useDebounce } from 'use-debounce';
import { SearchableMenuControlled, SearchableMenuMultiSelectList } from 'shared/domains/common-ui/SearchableMenu';
import { FileSystemEntryFilterDocument } from './FileSystemEntryFilter.gql';
import { ClearFilter } from '../components/ClearFilter';
import { NullOperator } from '../components/NullOperator';
import { OperatorSelector } from '../components/OperatorSelector';
import { useFilter } from '../useFilter';
export function FileSystemEntryFilter(props) {
    var _a;
    const { name, label } = props;
    const filter = useFilter(name);
    const [query, setQuery] = useDebounce('', 250);
    const selectedIds = (((_a = filter.value) === null || _a === void 0 ? void 0 : _a[filter.operator]) || []);
    const { data, previousData, error, loading } = useQuery(FileSystemEntryFilterDocument, {
        variables: {
            query,
            selectedIds,
        },
    });
    const { search, selectedEntries } = data || previousData || {};
    const isInitiallyLoading = !(previousData === null || previousData === void 0 ? void 0 : previousData.search) && loading;
    const { results } = search || {};
    const searchItems = (results === null || results === void 0 ? void 0 : results.map((result) => ({ id: result.entityId, title: result.name }))) || [];
    const selectedItems = (selectedEntries === null || selectedEntries === void 0 ? void 0 : selectedEntries.map((item) => ({ id: item.id, title: item.name }))) || [];
    const items = [...searchItems, ...selectedItems];
    const labelText = t({
        message: 'Select file',
        comment: 'Insights: File system entry filter label',
    });
    const [firstItem] = selectedEntries || [];
    const selectedCount = (selectedEntries === null || selectedEntries === void 0 ? void 0 : selectedEntries.length) || 0;
    const countText = selectedCount > 1 ? `+${selectedCount - 1}` : '';
    const itemsLabel = (firstItem === null || firstItem === void 0 ? void 0 : firstItem.name) || labelText;
    const handleResetSearch = () => setQuery('');
    const handleOnSelect = (item) => {
        const { id } = item;
        const selected = selectedIds.includes(id);
        if (!selected) {
            const newData = { [filter.operator]: [...selectedIds, id] };
            filter.setValue(newData);
            return;
        }
        if (selectedIds.length === 1) {
            filter.resetValue();
        }
        else {
            const newData = {
                [filter.operator]: selectedIds.filter((selectedItem) => selectedItem !== id),
            };
            filter.setValue(newData);
        }
    };
    return (<Skeleton loading={isInitiallyLoading}>
      <ButtonGroup>
        <OperatorSelector name={name} label={label} operators={[
            {
                id: 'in',
                menuItem: t({ message: 'In', comment: 'Search: Filter operator - in' }),
                label: t({ message: 'in', comment: 'Search: Filter operator - in' }),
                onSelect: () => filter.setValue({ in: undefined }),
            },
            {
                id: 'notIn',
                menuItem: t({ message: 'Not in', comment: 'Search: Filter operator - not in' }),
                label: t({ message: 'not in', comment: 'Search: Filter operator - not in' }),
                onSelect: () => filter.setValue({ notIn: undefined }),
            },
            {
                id: 'isNull',
                menuItem: t({ message: 'Has value', comment: 'Search: Filter operator - has value' }),
                label: t({ message: 'has', comment: 'Search: Filter operator - has value' }),
                onSelect: () => filter.setValue({ isNull: undefined }),
            },
        ]}/>

        {!isInitiallyLoading && filter.operator === 'isNull' && <NullOperator name={name}/>}

        {!isInitiallyLoading && (filter.operator === 'in' || filter.operator === 'notIn') && (<SearchableMenuControlled loading={loading} error={Boolean(error)} onSearch={setQuery} items={items} onReset={handleResetSearch} renderTarget={<FilterButton type="button" appearance={filter.isTouched ? 'draft' : 'default'}>
                {itemsLabel} {countText}
              </FilterButton>}>
            <SearchableMenuMultiSelectList selected={selectedIds} onSelect={(item) => handleOnSelect(item)} renderItem={({ title }) => title}/>
          </SearchableMenuControlled>)}

        <ClearFilter name={name}/>
      </ButtonGroup>
    </Skeleton>);
}
